import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { RootState, useAppDispatch, useAppSelector } from "../../../store";
import {
  deleteProductAPI,
  deleteVariantAPI,
  exportProductVariantsAPI,
  GetCategoryListAPI,
  GetFramePartOptionsAPI,
  GetMachineListAPI,
  getProductsListAPI,
  getVariantListAPI,
  regenerateVariantsAPI,
  updateVariantAPI,
  fetchProductsQueryFn,
  fetchVariantsQueryFn,
} from "./productApis";
import { svg } from "../../../Assets/svg";
import {
  Button,
  Col,
  Image,
  Row,
  Spin,
  Switch,
  Table,
  TableColumnsType,
  Dropdown,
  Menu,
  Form,
  Input,
  Select,
  Typography,
  message,
  Tag,
  Tooltip,
  Tabs,
} from "antd";
import { PaperClipOutlined } from "@ant-design/icons";
import FullScreenLoader from "../../Components/FullScreenLoader";
import NoDataMessage from "../../Components/NoData";
import CommonFilePreviewModal from "../../Components/CommonFilePreviewModal";
import { getFileNameFromUrl } from "../StockProcessManager/DrawingRequirementSection";
import {
  exportImportStatusClasess,
  exportImportStatusLabel,
  FinishType,
  formatFinishLabel,
  getFinishOptions,
  getProductImportActionLabel,
  PRODUCT_FILTER_KEY,
  PRODUCT_REFETCH_KEY,
  PRODUCT_REGENERATE_REFETCH_KEY,
  PRODUCT_SCROLL_KEY,
  ProductDropEnum,
  ProductDropLabels,
  ProductFinishEnum,
  ProductFinishLabels,
  getDropOptions,
  PRODUCT_TYPE_META,
  ProductComponentTypeLabels,
  ProductComponentTypeEnum,
  PRODUCT_DETAILS_VARIANT_SCROLL_KEY,
  PRODUCT_DETAILS_SCROLL_KEY,
  PRODUCT_DETAILS_REFETCH_KEY,
  PRODUCT_ACTIVE_VIEW_KEY,
} from "./productsTypes";
import {
  getAntdSortOrder,
  getFullAwsUrl,
  useDebounce,
} from "../../../helpers/utils";
import placeHolderImage from "../../../Assets/Images/placeholderImage.png";
import InfiniteScroll from "react-infinite-scroll-component";
import { CommonConfirmationModal } from "../../../Components/CommonConfirmationModal";
import { createVariantAPI } from "../PriceSheet/priceSheetApis";
import {
  PagePermissionNameData,
  usePermissionsChecker,
} from "../../../helpers/permissionModule";
import NoPermissionPage from "../../Components/NoPermissionPage";
import EditVariantModal from "./Components/EditVariantModal";
import { getDecryptedName } from "../../../helpers/bycrypt";
import {
  useQuery,
  useQueryClient,
  useInfiniteQuery,
} from "@tanstack/react-query";
import DescriptionWithShowMore from "../../../helpers/DescriptionWithShowMore";
import ProductParts from "./Components/ProductParts";
import ProductComponents from "./Components/ProductComponents";
import ProductProcesses from "./Components/ProductProcesses";
import ViewProductBOM from "../StockProcessManager/ProductBOM/ViewProductBOM";
// Session storage keys for ProductDetails

interface SelectOption {
  label: string;
  value: string | number;
}

type EditableColumnType = TableColumnsType<any>[number] & {
  editable?: boolean;
  inputType?: "text" | "number" | "select" | "multi-select";
  options?: SelectOption[]; // Because you are using options for dropdowns
};

export enum ProductDetailsSections {
  VARIANTS = "variants",
  PARTS = "parts",
  COMPONENTS = "components",
  PROCESS = "process",
  BOM = "bom",
}

// --- 1. Editable Cell Component ---
interface EditableCellProps extends React.HTMLAttributes<HTMLElement> {
  editing: boolean;
  dataIndex: string;
  title: string;
  inputType: "number" | "text" | "select" | "multi-select";
  record: any;
  index: number;
  selectOptions?: SelectOption[]; // For dropdowns
  validateFrameParts?: boolean;
  children: React.ReactNode;
}

const EditableCell: React.FC<EditableCellProps> = ({
  editing,
  dataIndex,
  title,
  inputType,
  record,
  index,
  children,
  selectOptions,
  validateFrameParts,
  ...restProps
}) => {
  // 1. Standard Input Node
  let inputNode = <Input className="form-control form-control-lg" />;

  if (inputType === "number") {
    inputNode = (
      <Input type="number" className="form-control form-control-lg" />
    );
  } else if (inputType === "select") {
    // Logic: If validation is FALSE and column is NOT 'finish', show Number Input instead of Select
    // (Existing logic from your code)
    if (
      validateFrameParts === false &&
      dataIndex !== "finish" &&
      dataIndex !== "secondary_finish" &&
      dataIndex !== "machines"
    ) {
      inputNode = (
        <Input
          type={
            [
              "bolts_per_footplate",
              "connecting_points",
              "holes_drilled",
            ].includes(dataIndex)
              ? "text"
              : "number"
          }
          className="form-control form-control-lg"
        />
      );
    } else {
      inputNode = (
        <Select
          className="w-100 custom-select-edit"
          suffixIcon={svg?.selectDownArrow}
        >
          {selectOptions?.map((opt) => (
            <Select.Option key={opt.value} value={opt.value}>
              {opt.label}
            </Select.Option>
          ))}
        </Select>
      );
    }
  } else if (inputType === "multi-select") {
    // ✅ NEW: Multi-Select Logic for Category/Machine
    inputNode = (
      <Select
        mode="multiple"
        className="w-100 custom-select-edit"
        suffixIcon={svg?.selectDownArrow}
        maxTagCount={1}
        allowClear
        placeholder="Select..."
      >
        {selectOptions?.map((opt) => (
          <Select.Option key={opt.value} value={opt.value}>
            {opt.label}
          </Select.Option>
        ))}
      </Select>
    );
  }

  // 2. Special Logic: If this is the "Color" cell, wrap it to watch "Finish"
  if (editing && (dataIndex === "color" || dataIndex === "secondary_color")) {
    const finishField =
      dataIndex === "secondary_color" ? "secondary_finish" : "finish";
    return (
      <td {...restProps}>
        <Form.Item
          noStyle
          shouldUpdate={(prevValues, currentValues) =>
            prevValues[finishField] !== currentValues[finishField]
          }
        >
          {({ getFieldValue }) => {
            // Get current value of finish from the form
            const finish = getFieldValue(finishField);

            // Check if it is Powder Coated
            const isPowderCoated =
              finish &&
              finish.replace(/_/g, " ").toLowerCase() ===
                ProductFinishEnum.POWDER_COATED;

            return (
              <Form.Item
                name={dataIndex}
                style={{ margin: 0 }}
                rules={[{ required: isPowderCoated, message: "Required" }]} // Make it required only if enabled
              >
                <Input
                  disabled={!isPowderCoated}
                  placeholder={!isPowderCoated ? "N/A" : "Enter Color"}
                  className="form-control form-control-lg"
                />
              </Form.Item>
            );
          }}
        </Form.Item>
      </td>
    );
  }

  // 3. Special Logic: If this is the "Custom Drop" cell, wrap it to watch "Drop"
  if (editing && dataIndex === "custom_drop") {
    return (
      <td {...restProps}>
        <Form.Item
          noStyle
          shouldUpdate={(prevValues, currentValues) =>
            prevValues.drop !== currentValues.drop
          }
        >
          {({ getFieldValue }) => {
            const drop = getFieldValue("drop");
            const isCustomDrop =
              drop === "custom" || drop === ProductDropEnum.CUSTOM;

            return (
              <Form.Item
                name="custom_drop"
                style={{ margin: 0 }}
                rules={[{ required: isCustomDrop, message: "Required" }]}
              >
                <Input
                  type="number"
                  disabled={!isCustomDrop}
                  placeholder={!isCustomDrop ? "N/A" : "Enter Drop"}
                  className="form-control form-control-lg"
                />
              </Form.Item>
            );
          }}
        </Form.Item>
      </td>
    );
  }

  // 4. Default Rendering for other cells
  return (
    <td {...restProps}>
      {editing ? (
        <div className="product-mm-cell page-form w-100">
          <Form.Item
            name={dataIndex}
            style={{ margin: 0 }}
            rules={[
              {
                required:
                  dataIndex !== "product_code" &&
                  dataIndex !== "color" &&
                  dataIndex !== "weight",
                message: `Required`,
              },
            ]}
          >
            {inputNode}
          </Form.Item>
        </div>
      ) : (
        children
      )}
    </td>
  );
};

// Intercept browser reloads and clear filter persistence so the page starts completely fresh
try {
  const navEntries = performance.getEntriesByType("navigation");
  const isReload =
    navEntries.length > 0 &&
    (navEntries[0] as PerformanceNavigationTiming).type === "reload";
  const isLegacyReload =
    performance.navigation && performance.navigation.type === 1;

  if (isReload || isLegacyReload) {
    sessionStorage.removeItem(PRODUCT_DETAILS_SCROLL_KEY);
    sessionStorage.removeItem(PRODUCT_DETAILS_VARIANT_SCROLL_KEY);
    sessionStorage.removeItem(PRODUCT_DETAILS_REFETCH_KEY);
  }
} catch (e) {}

const ProductDetails = () => {
  const USER_PERMISSIONS = usePermissionsChecker(
    PagePermissionNameData.PRODUCTS,
  );
  const { authLoading } = useAppSelector((state: RootState) => state.loading);

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { productTypeId, productId } = useParams();

  const fromView = useMemo(() => {
    const viewParam = searchParams.get("fromView");
    if (viewParam === "list" || viewParam === "project") return viewParam;
    const storageParam = sessionStorage.getItem(PRODUCT_ACTIVE_VIEW_KEY);
    if (storageParam === "list" || storageParam === "project")
      return storageParam;
    return "project";
  }, [searchParams]);
  const [form] = Form.useForm(); // ✅ Form Instance
  const queryClient = useQueryClient();
  const [isMount, setIsMount] = useState(true);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState<any>(null);

  // Scroll handling refs
  const scrollableDivRef = useRef<HTMLDivElement | null>(null);
  const scrollRestoredRef = useRef(false);
  const variantScrollRestoredRef = useRef(false); // Replace fetchProductsData with react-query
  const {
    data: productData,
    isLoading: loading,
    refetch: refetchProduct,
  } = useQuery({
    queryKey: ["product", productId],
    queryFn: async () => {
      const filters = {
        id: productId,
      };
      const response = await fetchProductsQueryFn(filters);
      return response?.products?.[0] || null;
    },
    enabled: !!productId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const [productToRegenrate, setProductToRegenrate] = useState<any>(null);
  const [regenerating, setRegenerating] = useState(false);

  const [activeView, setActiveView] = useState<string>(
    searchParams.get("entity") ||
      (fromView === "list"
        ? ProductDetailsSections.VARIANTS
        : ProductDetailsSections.PARTS),
  );

  const [visitedTabs, setVisitedTabs] = useState<string[]>([
    searchParams.get("entity") ||
      (fromView === "list"
        ? ProductDetailsSections.VARIANTS
        : ProductDetailsSections.PARTS),
  ]);

  useEffect(() => {
    if (!visitedTabs.includes(activeView)) {
      setVisitedTabs((prev) => [...prev, activeView]);
    }
  }, [activeView]);

  useEffect(() => {
    const entityParam = searchParams.get("entity");
    if (!entityParam) {
      setSearchParams(
        (prev) => {
          const nextParams = new URLSearchParams(prev);
          nextParams.set(
            "entity",
            fromView === "list"
              ? ProductDetailsSections.VARIANTS
              : ProductDetailsSections.PARTS,
          );
          return nextParams;
        },
        { replace: true },
      );
    }
  }, []);

  // --- Variant Search and Sorting States ---
  const VARIANT_LIMIT = 15;
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 500);

  const [sorterState, setSorterState] = useState<{
    field: string | null;
    order: "asc" | "desc" | null;
  }>({ field: null, order: null });

  // --- Variant Query Filters ---
  const variantQueryFilters = {
    page: 1,
    limit: VARIANT_LIMIT,
    sortBy: sorterState.field,
    sortOrder: sorterState.order,
    search: debouncedSearch,
  };

  // --- Variants Infinite Query ---
  const {
    data: variantsQueryData,
    fetchNextPage: fetchNextVariantsPage,
    hasNextPage: hasNextVariantsPage,
    isFetchingNextPage: isFetchingNextVariantsPage,
    isLoading: variantLoading,
    isFetching: isFetchingVariants,
    refetch: refetchVariants,
  } = useInfiniteQuery({
    queryKey: ["variants", productId, variantQueryFilters],
    queryFn: ({ pageParam = 1, signal }) => {
      if (!productId) throw new Error("Product ID is required");
      const filters = {
        ...variantQueryFilters,
        page: pageParam,
      };
      return fetchVariantsQueryFn(productId, filters, signal);
    },
    getNextPageParam: (lastPage: any, allPages) => {
      const fetched = allPages.reduce(
        (acc: number, p: any) => acc + (p?.variants?.length || 0),
        0,
      );
      const total = lastPage?.pagination?.total || 0;
      return fetched < total ? allPages.length + 1 : undefined;
    },
    enabled: !!productId,
    staleTime: 5 * 60 * 1000,
    initialPageParam: 1,
  });

  const variantsList: any[] = variantsQueryData
    ? variantsQueryData.pages.flatMap((p: any) => p?.variants || [])
    : [];

  const activeAdditionalFields = useMemo(() => {
    const fields = [
      "max_load_per_bay",
      "max_load_per_pair",
      "max_load_per_shelf",
    ];
    return fields.filter((key) => variantsList?.[0] && key in variantsList[0]);
  }, [variantsList]);
  const variantTotal: number =
    variantsQueryData?.pages?.[0]?.pagination?.total || 0;
  const variantHasMore = hasNextVariantsPage;

  const [machineLoading, setMachineLoading] = useState(false);
  const [machines, setMachines] = useState<any[]>([]);
  const [machinePage, setMachinePage] = useState(1);
  const [hasMoreMachine, setHasMoreMachine] = useState(true);

  const [exporting, setExporting] = useState(false);

  // edit vaiant in modal
  const [editVariantModal, setEditVariantModal] = useState(false);
  const [editVariantModalData, setEditVariantModalData] = useState<any>(null);

  //Frame Parts Options
  const [framePartOptions, setFramePartOptions] = useState<{
    width: string[];
    height: string[];
    reinforced_height: string[];
    bolts_per_footplate: string[];
    depth: string[];
    thickness: string[];
    length: string[];
    connecting_points: string[];
    holes_drilled: string[];
  }>({
    width: [],
    height: [],
    reinforced_height: [],
    bolts_per_footplate: [],
    depth: [],
    thickness: [],
    length: [],
    connecting_points: [],
    holes_drilled: [],
  });

  // --- Edit States ---
  const [editingKey, setEditingKey] = useState<string>(""); // ✅ Track which row is editing
  const isEditing = (record: any) => record._id === editingKey;

  //Delete States
  const [variantIdToDelete, setVariantIdToDelete] = useState<string | null>(
    null,
  );
  const [categories, setCategories] = useState<any[]>([]);
  const [idToDelete, setIdToDelete] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const [toggleStatusSettings, setToggleStatusSettings] = useState<{
    isModalOpen: boolean;
    id: string;
    toActivate: boolean;
  }>({ isModalOpen: false, id: "", toActivate: false });
  const [toggling, setToggling] = useState(false);

  // Restore Main Scroll
  useEffect(() => {
    if (
      !loading &&
      scrollableDivRef.current &&
      !scrollRestoredRef.current &&
      !isMount
    ) {
      const savedScroll = sessionStorage.getItem(PRODUCT_DETAILS_SCROLL_KEY);
      if (savedScroll) {
        scrollableDivRef.current.scrollTop = Number(savedScroll);
      }
      scrollRestoredRef.current = true;
    }
  }, [loading, isMount]);

  // Restore Variant Scroll
  useEffect(() => {
    if (
      !variantLoading &&
      variantsList.length > 0 &&
      !variantScrollRestoredRef.current &&
      !isMount
    ) {
      const savedVariantScroll = sessionStorage.getItem(
        PRODUCT_DETAILS_VARIANT_SCROLL_KEY,
      );
      if (savedVariantScroll) {
        const target = document.getElementById("variantScrollableDiv");
        if (target) {
          target.scrollTop = Number(savedVariantScroll);
        }
      }
      variantScrollRestoredRef.current = true;
    }
  }, [variantLoading, variantsList.length, isMount]);

  const saveScrollPositions = () => {
    if (scrollableDivRef.current) {
      sessionStorage.setItem(
        PRODUCT_DETAILS_SCROLL_KEY,
        String(scrollableDivRef.current.scrollTop),
      );
    }
    const variantTarget = document.getElementById("variantScrollableDiv");
    if (variantTarget) {
      sessionStorage.setItem(
        PRODUCT_DETAILS_VARIANT_SCROLL_KEY,
        String(variantTarget.scrollTop),
      );
    }
  };

  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => setIsMount(false), 20);
    return () => {
      clearTimeout(timer);
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const fetchCategoriesData = () => {
    dispatch(
      GetCategoryListAPI({
        filters: { product_type: productData?.master_product as any },
        onSuccess: (data) => {
          const list = data?.categories || [];
          const formatted = list.map((item: any) => ({
            label: item.name,
            value: item._id || item.name,
          }));
          setCategories((prev) => formatted);
        },
        onError: () => {},
      }),
    );
  };

  const fetchMachinesData = useCallback(() => {
    setMachineLoading(true);
    dispatch(
      GetMachineListAPI({
        filters: {
          page: 1,
          limit: 5000, // Fetch all (or a very large number)
          search: "", // No search filter initially
        },
        onSuccess: (data) => {
          const list = data?.machines || [];
          const formatted = list.map((item: any) => ({
            label: item.name,
            value: item._id || item.name,
            style: { marginBottom: "10px" },
          }));
          setMachines(formatted); // Replace entire list
          setMachineLoading(false);
        },
        onError: () => setMachineLoading(false),
      }),
    );
  }, [dispatch]);

  useEffect(() => {
    if (productData?.master_product) {
      fetchCategoriesData();
    }
    fetchMachinesData();
  }, [productData]);

  // Handle refetch on mount if returning from edit/create
  useEffect(() => {
    if (sessionStorage.getItem(PRODUCT_DETAILS_REFETCH_KEY) === "1") {
      sessionStorage.removeItem(PRODUCT_DETAILS_REFETCH_KEY);
      queryClient.invalidateQueries({ queryKey: ["product", productId] });
      queryClient.invalidateQueries({ queryKey: ["variants", productId] });
    }
  }, [productId, queryClient]);

  // Update product variables when variants data changes
  useEffect(() => {
    const firstPageData = variantsQueryData?.pages?.[0];
    if (firstPageData?.variables) {
      queryClient.setQueryData(["product", productId], (prev: any) =>
        prev
          ? {
              ...prev,
              variables: firstPageData.variables,
            }
          : { variables: firstPageData.variables },
      );
    }
  }, [variantsQueryData, productId, queryClient]);

  const handleLoadMoreVariants = () => {
    if (hasNextVariantsPage && !isFetchingNextVariantsPage) {
      fetchNextVariantsPage();
    }
  };

  const searchBarHandler = (e: any) => {
    setSearch(e.target.value);
  };

  const searchBarClean = () => {
    setSearch("");
  };

  const fetchAvailableParts = () => {
    if (!productId) return;
    dispatch(
      GetFramePartOptionsAPI({
        filters: {
          product_id: productId,
        },
        onSuccess: (data: any) => {
          setFramePartOptions(data);
        },
        onError: () => {},
      }),
    );
  };

  // useEffect(() => {
  //   fetchAvailableParts();
  // }, [productId]);

  const dynamicFinishOptions = useMemo(() => {
    // 1. Get the raw array from backend data
    const availableFinishes = productData?.variant_columns?.finish || [];

    // 2. Map using your existing helper/labels
    return availableFinishes.map((f: string) => {
      // Cast the string from backend to your Enum type for type-safe lookup
      const finishKey = f as ProductFinishEnum;

      return {
        value: f,
        // Use existing labels if key exists, otherwise fallback to formatting helper
        label: ProductFinishLabels[finishKey] || formatFinishLabel(f),
      };
    });
  }, [productData]);

  const dynamicSecondaryFinishOptions = useMemo(() => {
    const availableFinishes =
      productData?.variant_columns?.secondary_finish || [];

    return availableFinishes.map((f: string) => {
      const finishKey = f as ProductFinishEnum;

      return {
        value: f,
        label: ProductFinishLabels[finishKey] || formatFinishLabel(f),
      };
    });
  }, [productData]);

  // --- Inline Edit Functions ---
  const edit = (record: any) => {
    setEditVariantModalData(record);
    setEditVariantModal(true);
    // form.setFieldsValue({
    //   ...record,
    //   categories: record.categories?.map((c: any) => c._id || c) || [],
    //   machines: record.machines?.map((m: any) => m._id || m) || [],
    // });
    // setEditingKey(record._id);
  };

  const cancel = () => {
    if (editingKey.toString().startsWith("NEW_")) {
      // Remove temp variant from query cache
      queryClient.setQueryData(
        ["variants", productId, variantQueryFilters],
        (old: any) => {
          if (!old) return old;
          return {
            ...old,
            pages: old.pages.map((page: any) => ({
              ...page,
              variants: (page.variants || []).filter(
                (item: any) => item._id !== editingKey,
              ),
            })),
          };
        },
      );
    }
    setEditingKey("");
  };

  const save = async (key: React.Key) => {
    try {
      const row = await form.validateFields();

      // 1. Convert Numeric Fields (Schema requires Numbers)
      const numericFields = [
        "width",
        "height",
        "reinforced_height",
        "depth",
        "thickness",
        "weight",
        "length",
        "max_load_per_bay",
        "max_load_per_pair",
        "max_load_per_shelf",
      ];
      numericFields.forEach((field) => {
        const val = row[field];
        if (val !== undefined && val !== null && val !== "") {
          row[field] = Number(val);
        } else {
          // If field is present but empty, set to null
          if (row.hasOwnProperty(field)) row[field] = null;
        }
      });

      // 2. Sanitize Strings (Optional: ensure empty strings are null)
      const stringFields = [
        "variant_name",
        "product_code",
        "finish",
        "color",
        "first_beam_level",
        "frame_type",
        "foot_type",
        "shelf_reinforcers",
        "crossties",
        "pallet_supports",
        "chipboard_supports",
        "dividers_per_shelf",
        "v_type",
        "drop",
        "custom_drop",
      ];

      stringFields.forEach((field) => {
        if (row[field] === "") {
          row[field] = null;
        }
      });
      if (!row.categories) row.categories = [];

      // Convert machine string back to an array for the backend and local state
      if (row.machines === undefined || row.machines === null) {
        row.machines = [];
      } else if (!Array.isArray(row.machines)) {
        row.machines = [row.machines];
      }
      console.log("Saving Variant Payload:", row);

      if (key.toString().startsWith("NEW_")) {
        // --- CREATE MODE ---
        // Ensure mandatory fields for creation
        const payload = {
          ...row,
          product_id: productId,
          is_active: true, // Default active for new variants
        };

        dispatch(
          createVariantAPI({
            payload,
            onSuccess: (data: any) => {
              // Update query cache with new variant
              queryClient.setQueryData(
                ["variants", productId, variantQueryFilters],
                (old: any) => {
                  if (!old) return old;
                  return {
                    ...old,
                    pages: old.pages.map((page: any, pageIndex: number) => {
                      if (pageIndex === 0) {
                        // Replace temp variant with real data in first page
                        const updatedVariants = (page.variants || []).map(
                          (item: any) =>
                            item._id === key ? data.variant || data : item,
                        );
                        return {
                          ...page,
                          variants: updatedVariants,
                          pagination: {
                            ...page.pagination,
                            total: (page.pagination?.total || 0) + 1,
                          },
                        };
                      }
                      return page;
                    }),
                  };
                },
              );
              setEditingKey("");
            },
            onError: () => {
              // Keep row in edit mode
            },
          }),
        );
      } else {
        // --- UPDATE MODE ---
        dispatch(
          updateVariantAPI({
            variant_id: key as string,
            payload: row,
            onSuccess: (data: any) => {
              // Update query cache with updated variant
              queryClient.setQueryData(
                ["variants", productId, variantQueryFilters],
                (old: any) => {
                  if (!old) return old;
                  return {
                    ...old,
                    pages: old.pages.map((page: any) => ({
                      ...page,
                      variants: (page.variants || []).map((item: any) =>
                        item._id === key ? data.variant || data : item,
                      ),
                    })),
                  };
                },
              );
              setEditingKey("");
            },
            onError: () => {},
          }),
        );
      }
    } catch (errInfo) {
      console.log("Validate Failed:", errInfo);
    }
  };

  const duplicateVariant = (record: any) => {
    // 1. Create a unique Temp ID
    const tempId = `NEW_${Date.now()}`;

    // 2. Create the new object (copy of record)
    const newVariant = {
      ...record,
      _id: tempId, // Temporary ID
      product_code: `${record.product_code || ""}-COPY`, // Visual indicator
      is_active: true, // Default to active?
    };

    // 3. Add to query cache at the beginning of first page
    queryClient.setQueryData(
      ["variants", productId, variantQueryFilters],
      (old: any) => {
        if (!old) return old;
        return {
          ...old,
          pages: old.pages.map((page: any, pageIndex: number) => {
            if (pageIndex === 0) {
              return {
                ...page,
                variants: [newVariant, ...(page.variants || [])],
              };
            }
            return page;
          }),
        };
      },
    );

    // 4. Pre-fill form and trigger edit
    form.setFieldsValue({ ...newVariant });
    setEditingKey(tempId);
  };

  const handleDeleteConfirm = () => {
    if (!variantIdToDelete) return;

    setDeleting(true);

    dispatch(
      deleteVariantAPI({
        variant_id: variantIdToDelete,
        onSuccess: () => {
          setDeleting(false);
          // Remove variant from query cache
          queryClient.setQueryData(
            ["variants", productId, variantQueryFilters],
            (old: any) => {
              if (!old) return old;
              return {
                ...old,
                pages: old.pages.map((page: any) => ({
                  ...page,
                  variants: (page.variants || []).filter(
                    (p: any) => p._id !== variantIdToDelete,
                  ),
                  pagination: {
                    ...page.pagination,
                    total: Math.max(0, (page.pagination?.total || 0) - 1),
                  },
                })),
              };
            },
          );
          sessionStorage.removeItem(PRODUCT_FILTER_KEY);
          sessionStorage.setItem(PRODUCT_REFETCH_KEY, "1");
          setVariantIdToDelete(null);
        },
        onError: () => {
          setDeleting(false);
        },
      }),
    );
  };

  const handleCloseDeleteModal = () => {
    setVariantIdToDelete(null);
  };

  const items = useMemo(() => {
    return [
      ...(fromView === "list"
        ? [
            {
              label: `Variants`,
              key: ProductDetailsSections.VARIANTS,
            },
          ]
        : []),
      {
        label: `Parts`,
        key: ProductDetailsSections.PARTS,
      },
      {
        label: `Components`,
        key: ProductDetailsSections.COMPONENTS,
      },
      {
        label: `Process`,
        key: ProductDetailsSections.PROCESS,
      },
      {
        label: `BOM`,
        key: ProductDetailsSections.BOM,
      },
    ];
  }, [fromView]);

  const formatOptions = (arr: (string | number)[]) =>
    arr?.map((item) => ({ label: String(item), value: item })) || [];

  // --- Column Configuration ---
  const columns: EditableColumnType[] = [
    {
      title: "",
      dataIndex: "is_sage_synced",
      key: "is_sage_synced",
      align: "center",
      width: 50,
      render: (is_sage_synced: any, record: any) => {
        return (
          <Tooltip title={is_sage_synced ? "Data Synced" : "Data Not Synced"}>
            <span className="d-flex align-items-center justify-content-center svg-20 cursor-pointer">
              {is_sage_synced ? svg?.sageIconSync : svg?.sageNonSyncIcon}
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: "Product Code",
      dataIndex: "product_code",
      key: "product_code",
      editable: true,
      inputType: "text",
      width: "8rem",
      sorter: true,
      sortOrder: getAntdSortOrder("product_code", sorterState),
      render: (_, record) => (
        <span className="fw-bold text-dark">{record.product_code || "-"}</span>
      ),
    },
    {
      title: "Variant Title",
      dataIndex: "variant_name",
      key: "variant_name",
      editable: true,
      inputType: "text",
      width: "14rem",
      sorter: true,
      sortOrder: getAntdSortOrder("variant_name", sorterState),
      render: (_, record) => (
        <span className="fw-bold text-dark">{record.variant_name || "-"}</span>
      ),
    },
    {
      title: "Height (mm)",
      dataIndex: "height",
      key: "height",
      editable: false,
      inputType: "select",
      options: formatOptions(framePartOptions.height),
      width: "8rem",
      sorter: true,
      render: (val: any) => val ?? "-",
      sortOrder: getAntdSortOrder("height", sorterState),
    },
    {
      title: "Reinforced Height (mm)",
      dataIndex: "reinforced_height",
      key: "reinforced_height",
      editable: false,
      inputType: "select",
      options: formatOptions(framePartOptions.reinforced_height),
      width: "12rem",
      sorter: true,
      sortOrder: getAntdSortOrder("reinforced_height", sorterState),
      render: (val: any) => val ?? "-",
    },
    {
      title: "Front Upright Height (mm)",
      dataIndex: "front_upright_height",
      key: "front_upright_height",
      editable: false,
      inputType: "select",
      width: "12rem",
      sorter: true,
      sortOrder: getAntdSortOrder("front_upright_height", sorterState),
      render: (val: any) => val ?? "-",
    },
    {
      title: "Rear Upright Height (mm)",
      dataIndex: "rear_upright_height",
      key: "rear_upright_height",
      editable: false,
      inputType: "select",
      width: "12rem",
      sorter: true,
      sortOrder: getAntdSortOrder("rear_upright_height", sorterState),
      render: (val: any) => val ?? "-",
    },
    {
      title: "Width (mm)",
      dataIndex: "width",
      key: "width",
      editable: false,
      inputType: "select",
      options: formatOptions(framePartOptions.width),
      width: "8rem",
      sorter: true,
      sortOrder: getAntdSortOrder("width", sorterState),
      render: (val: any) => val ?? "-",
    },
    {
      title: "Depth (mm)",
      dataIndex: "depth",
      key: "depth",
      editable: false,
      inputType: "select",
      options: formatOptions(framePartOptions.depth),
      width: "8rem",
      sorter: true,
      sortOrder: getAntdSortOrder("depth", sorterState),
      render: (val: any) => val ?? "-",
    },
    {
      title: "Length (mm)",
      dataIndex: "length",
      key: "length",
      sorter: true,
      sortOrder: getAntdSortOrder("length", sorterState),
      editable: false,
      inputType: "number",
      options: formatOptions(framePartOptions.length),
      width: "8rem",
      render: (val: any) => val ?? "-",
    },
    {
      title: "Thickness (mm)",
      dataIndex: "thickness",
      key: "thickness",
      editable: false,
      inputType: "select",
      options: formatOptions(framePartOptions.thickness),
      width: "9rem",
      sorter: true,
      sortOrder: getAntdSortOrder("thickness", sorterState),
      render: (val: any) => val ?? "-",
    },
    {
      title: "Gauge (mm)",
      dataIndex: "gauge",
      key: "gauge",
      editable: false,
      inputType: "text",
      sorter: true,
      sortOrder: getAntdSortOrder("gauge", sorterState),
      render: (val: any) => val ?? "-",
      width: "10rem",
    },
    {
      title: "Level Width (mm)",
      dataIndex: "level_width",
      key: "level_width",
      editable: false,
      inputType: "text",
      sorter: true,
      sortOrder: getAntdSortOrder("level_width", sorterState),
      render: (val: any) => val ?? "-",
      width: "10rem",
    },
    {
      title: "Level Depth (mm)",
      dataIndex: "level_depth",
      key: "level_depth",
      editable: false,
      inputType: "text",
      sorter: true,
      sortOrder: getAntdSortOrder("level_depth", sorterState),
      render: (val: any) => val ?? "-",
      width: "10rem",
    },
    {
      title: "Bolts per Footplate",
      dataIndex: "bolts_per_footplate",
      key: "bolts_per_footplate",
      editable: false,
      inputType: "select",
      options: formatOptions(framePartOptions.bolts_per_footplate),
      width: "12rem",
      sorter: true,
      sortOrder: getAntdSortOrder("bolts_per_footplate", sorterState),
      render: (val: any) => val ?? "-",
    },
    {
      title: "Dividers per Shelf",
      dataIndex: "dividers_per_shelf",
      key: "dividers_per_shelf",
      editable: false,
      inputType: "text",
      width: "12rem",
      sorter: true,
      sortOrder: getAntdSortOrder("dividers_per_shelf", sorterState),
      render: (val: any) => val ?? "-",
    },
    {
      title: "Connecting Points",
      dataIndex: "connecting_points",
      key: "connecting_points",
      editable: false,
      inputType: "select",
      options: formatOptions(framePartOptions.connecting_points),
      width: "12rem",
      sorter: true,
      sortOrder: getAntdSortOrder("connecting_points", sorterState),
      render: (val: any) => val ?? "-",
    },
    {
      title: "Holes Drilled",
      dataIndex: "holes_drilled",
      key: "holes_drilled",
      editable: false,
      inputType: "select",
      options: formatOptions(framePartOptions.holes_drilled),
      width: "12rem",
      sorter: true,
      sortOrder: getAntdSortOrder("holes_drilled", sorterState),
      render: (val: any) => val ?? "-",
    },
    {
      title: "1st Beam Level (mm)",
      dataIndex: "first_beam_level",
      key: "first_beam_level",
      editable: false,
      inputType: "text",
      sorter: true,
      sortOrder: getAntdSortOrder("first_beam_level", sorterState),
      render: (val: any) => val ?? "-",
      width: "8rem",
    },
    {
      title: "Frame Type",
      dataIndex: "frame_type",
      key: "frame_type",
      editable: false,
      inputType: "text",
      sorter: true,
      sortOrder: getAntdSortOrder("frame_type", sorterState),
      render: (val: any) => val ?? "-",
      width: "10rem",
    },
    {
      title: "Foot Type",
      dataIndex: "foot_type",
      key: "foot_type",
      editable: false,
      inputType: "text",
      sorter: true,
      sortOrder: getAntdSortOrder("foot_type", sorterState),
      render: (val: any) => val ?? "-",
      width: "10rem",
    },
    {
      title: "Shelf Reinforcers",
      dataIndex: "shelf_reinforcers",
      key: "shelf_reinforcers",
      editable: false,
      inputType: "text",
      sorter: true,
      sortOrder: getAntdSortOrder("shelf_reinforcers", sorterState),
      render: (val: any) => val ?? "-",
      width: "10rem",
    },
    {
      title: "Crossties",
      dataIndex: "crossties",
      key: "crossties",
      editable: false,
      inputType: "text",
      sorter: true,
      sortOrder: getAntdSortOrder("crossties", sorterState),
      render: (val: any) => val ?? "-",
      width: "10rem",
    },
    {
      title: "Pallet Supports",
      dataIndex: "pallet_supports",
      key: "pallet_supports",
      editable: false,
      inputType: "text",
      sorter: true,
      sortOrder: getAntdSortOrder("pallet_supports", sorterState),
      render: (val: any) => val ?? "-",
      width: "10rem",
    },
    {
      title: "Chipboard Supports",
      dataIndex: "chipboard_supports",
      key: "chipboard_supports",
      editable: false,
      inputType: "text",
      sorter: true,
      sortOrder: getAntdSortOrder("chipboard_supports", sorterState),
      render: (val: any) => val ?? "-",
      width: "10rem",
    },
    {
      title: "Type",
      dataIndex: "v_type",
      key: "v_type",
      editable: false,
      inputType: "text",
      sorter: true,
      sortOrder: getAntdSortOrder("v_type", sorterState),
      render: (val: any) => val ?? "-",
      width: "8rem",
    },
    {
      title: "Drop",
      dataIndex: "drop",
      key: "drop",
      editable: false,
      inputType: "select",
      options: getDropOptions(),
      width: "9rem",
      sorter: true,
      sortOrder: getAntdSortOrder("drop", sorterState),
      render: (text: ProductDropEnum) => (
        <span className="">{ProductDropLabels[text] || "-"}</span>
      ),
    },
    {
      title: "Custom Drop (mm)",
      dataIndex: "custom_drop",
      key: "custom_drop",
      editable: false,
      inputType: "number",
      sorter: true,
      sortOrder: getAntdSortOrder("custom_drop", sorterState),
      render: (val: any) => val ?? "-",
      width: "10rem",
    },
    {
      title: "1st Shelf Level (mm)",
      dataIndex: "first_shelf_level",
      key: "first_shelf_level",
      editable: false,
      inputType: "text",
      sorter: true,
      sortOrder: getAntdSortOrder("first_shelf_level", sorterState),
      render: (val: any) => val ?? "-",
      width: "10rem",
    },
    {
      title:
        productData?.finish_label &&
        productData?.variables?.secondary_finish
          ? `${productData.finish_label} Finish`
          : "Finish",
      dataIndex: "finish",
      key: "finish",
      editable: false,
      inputType: "select",
      options: dynamicFinishOptions,
      width: "9rem",
      sorter: true,
      sortOrder: getAntdSortOrder("finish", sorterState),
      render: (text: ProductFinishEnum) => (
        <span className="">{ProductFinishLabels[text] || "-"}</span>
      ),
    },
    {
      title: "Color",
      dataIndex: "color",
      key: "color",
      editable: false,
      inputType: "text",
      sorter: true,
      sortOrder: getAntdSortOrder("color", sorterState),
      render: (val: any) => val ?? "-",
      width: "8rem",
    },
    {
      title: productData?.secondary_finish_label
        ? `${productData.secondary_finish_label} Finish`
        : "Secondary Finish",
      dataIndex: "secondary_finish",
      key: "secondary_finish",
      editable: false,
      inputType: "select",
      options: dynamicSecondaryFinishOptions,
      width: "9rem",
      sorter: true,
      sortOrder: getAntdSortOrder("secondary_finish", sorterState),
      render: (text: ProductFinishEnum) => (
        <span className="">{ProductFinishLabels[text] || "-"}</span>
      ),
    },
    {
      title: "Color",
      dataIndex: "secondary_color",
      key: "secondary_color",
      editable: false,
      inputType: "text",
      sorter: true,
      sortOrder: getAntdSortOrder("secondary_color", sorterState),
      render: (val: any) => val ?? "-",
      width: "8rem",
    },
    {
      title: "Max Load per Bay",
      dataIndex: "max_load_per_bay",
      key: "max_load_per_bay",
      editable: true,
      inputType: "number" as const,
      sorter: true,
      sortOrder: getAntdSortOrder("max_load_per_bay", sorterState),
      render: (val: any) => val ?? "-",
      width: "10rem",
    },
    {
      title: "Max Load per Pair",
      dataIndex: "max_load_per_pair",
      key: "max_load_per_pair",
      editable: true,
      inputType: "number" as const,
      sorter: true,
      sortOrder: getAntdSortOrder("max_load_per_pair", sorterState),
      render: (val: any) => val ?? "-",
      width: "10rem",
    },
    {
      title: "Max Load per Shelf",
      dataIndex: "max_load_per_shelf",
      key: "max_load_per_shelf",
      editable: true,
      inputType: "number" as const,
      sorter: true,
      sortOrder: getAntdSortOrder("max_load_per_shelf", sorterState),
      render: (val: any) => val ?? "-",
      width: "10rem",
    },

    ...(productData?.coil_meterage?.is_active
      ? [
          {
            title: "Coil ID",
            dataIndex: "coil_id",
            key: "coil_id",
            editable: true,
            inputType: "text" as const,
            sorter: true,
            sortOrder: getAntdSortOrder("coil_id", sorterState),
            render: (val: any) => val?.code ?? val ?? "-",
            width: "8rem",
          },
          {
            title: "Coil Metreage",
            dataIndex: "coil_meterage_used",
            key: "coil_meterage_used",
            editable: true,
            inputType: "number" as const,
            sorter: true,
            sortOrder: getAntdSortOrder("coil_meterage_used", sorterState),
            render: (val: any) => val ?? "-",
            width: "10rem",
          },
        ]
      : []),
    {
      title: "Weight (kg)",
      dataIndex: "weight",
      key: "weight",
      editable: true,
      inputType: "number",
      sorter: true,
      sortOrder: getAntdSortOrder("weight", sorterState),
      render: (weight: number) => <span>{weight ?? "-"}</span>,
      width: "7rem",
    },
    // {
    //   title: "Category",
    //   dataIndex: "categories",
    //   key: "categories",
    //   width: "12rem",
    //   editable: true,
    //   inputType: "multi-select",
    //   options: categories,
    //   // sorter: true,
    //   // sortOrder: getAntdSortOrder("categories", sorterState),
    //   render: (val: any) => {
    //     // Normalize to array
    //     const arr = Array.isArray(val) ? val : val ? [val] : [];
    //     if (arr.length === 0) return "-";

    //     return arr
    //       .map((c) =>
    //         typeof c === "object"
    //           ? c.name
    //           : categories.find((cat) => cat.value === c)?.label,
    //       )
    //       .filter(Boolean)
    //       .join(", ");
    //   },
    // },
    // {
    //   title: "Machine",
    //   dataIndex: "machines",
    //   key: "machines",
    //   width: "12rem",
    //   editable: true,
    //   inputType: "multi-select",
    //   options: machines,
    //   // sorter: true,
    //   // sortOrder: getAntdSortOrder("machines", sorterState),
    //   render: (val: any) => {
    //     // Normalize to array
    //     const arr = Array.isArray(val) ? val : val ? [val] : [];
    //     if (arr.length === 0) return "-";

    //     return arr
    //       .map((m) =>
    //         typeof m === "object"
    //           ? m.name
    //           : // Fallback for both machines state structures (value/label vs _id/name)
    //             machines.find((mach) => mach.value === m || mach._id === m)
    //               ?.label || machines.find((mach) => mach._id === m)?.name,
    //       )
    //       .filter(Boolean)
    //       .join(", ");
    //   },
    // },
    // {
    //   title: "Status",
    //   dataIndex: "is_active",
    //   key: "is_active",
    //   width: 100,
    //   render: (isActive: boolean, record) => {
    //     // Disable switch when editing to prevent conflict
    //     const editable = isEditing(record);
    //     return (
    //       <Switch
    //         disabled={editable}
    //         className="customSwitch"
    //         checked={!!isActive}
    //         onChange={(checked, e) => {
    //           e.preventDefault();
    //           e.stopPropagation();
    //           setToggleStatusSettings({
    //             isModalOpen: true,
    //             id: record._id,
    //             toActivate: checked,
    //           });
    //         }}
    //       />
    //     );
    //   },
    // },
    {
      title: "Action",
      key: "action",
      width: 100,
      fixed: "right",
      align: "center",
      render: (_, record) => {
        const editable = isEditing(record);

        return editable ? (
          <div className="d-flex gap-2 justify-content-center">
            <span
              onClick={() => save(record._id)}
              className="cursor-pointer svg-32"
              title="Save"
            >
              {svg?.checkButton}
            </span>
            <span
              onClick={cancel}
              className="cursor-pointer svg-32"
              title="Cancel"
            >
              {svg?.crossButton}
            </span>
          </div>
        ) : (
          <>
            <div className="d-flex justify-content-center gap-2">
              {USER_PERMISSIONS.MODIFY && (
                <Switch
                  disabled={editable}
                  className="customSwitch"
                  checked={!!record.is_active}
                  onChange={(checked, e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setToggleStatusSettings({
                      isModalOpen: true,
                      id: record._id,
                      toActivate: checked,
                    });
                  }}
                />
              )}

              <Dropdown
                overlay={() => MoreOptionsMenu(record)}
                trigger={["click"]}
                placement="bottomRight"
                className="cursor-pointer"
                overlayClassName="moreOptionDropdown"
              >
                <span className="svg-32 svg-outline-primary" role="button">
                  {svg?.tableMoreOption}
                </span>
              </Dropdown>
            </div>
          </>
        );
      },
    },
  ];

  const visibleColumns = columns.filter((col) => {
    const dynamicKeys = [
      "width",
      "height",
      "reinforced_height",
      "front_upright_height",
      "rear_upright_height",
      "depth",
      "length",
      "thickness",
      "bolts_per_footplate",
      "dividers_per_shelf",
      "finish",
      "secondary_finish",
      "connecting_points",
      "holes_drilled",
      "first_beam_level",
      "frame_type",
      "foot_type",
      "shelf_reinforcers",
      "crossties",
      "pallet_supports",
      "chipboard_supports",
      "v_type",
      "drop",
      "custom_drop",
      "gauge",
      "level_width",
      "level_depth",
      "first_shelf_level",
    ];

    const columnKey = col.key as string;

    if (dynamicKeys.includes(columnKey)) {
      return productData?.variables?.[columnKey] === true;
    }

    // ✅ NEW: Logic for Additional Fields (Max Load per Bay/Pair/Shelf)
    // Render the field if the key is present in the backend data (variant object)
    const additionalFields = [
      "max_load_per_bay",
      "max_load_per_pair",
      "max_load_per_shelf",
    ];
    if (additionalFields.includes(columnKey)) {
      return variantsList?.[0] && columnKey in variantsList[0];
    }

    if (columnKey === "color") {
      // ✅ NEW: Only show color column if "powder_coated" is in the variant_columns > finish array
      const hasPowderCoated = productData?.variant_columns?.finish?.some(
        (f: string) =>
          f.includes("powder") || f === ProductFinishEnum.POWDER_COATED,
      );
      return hasPowderCoated;
    }
    if (columnKey === "secondary_color") {
      const hasPowderCoated =
        productData?.variant_columns?.secondary_finish?.some(
          (f: string) =>
            f.includes("powder") || f === ProductFinishEnum.POWDER_COATED,
        );
      return hasPowderCoated;
    }
    if (columnKey === "custom_drop") {
      // Show custom_drop column if any variants use 'custom' drop
      const hasCustomDrop = productData?.variant_columns?.drop?.some(
        (f: string) => f === "custom" || f === ProductDropEnum.CUSTOM,
      );
      return hasCustomDrop;
    }
    return true;
  });

  // --- Map Columns to Editable ---
  const mergedColumns = visibleColumns.map((col: any) => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record: any) => ({
        record,
        inputType: col.inputType,
        dataIndex: col.dataIndex,
        title: col.title,
        editing: isEditing(record),
        selectOptions: col.options,
        validateFrameParts: productData?.validate_frame_parts,
      }),
    };
  });

  const MainMoreOptionsMenu = (data: any) => {
    const isDefaultProduct = data.is_default ?? false;
    return (
      <Menu>
        {/* ✅ Modify Guard */}
        {USER_PERMISSIONS.MODIFY && (
          <Menu.Item
            key="1"
            onClick={() => {
              saveScrollPositions();
              navigate(`/products/edit/${data._id}`);
            }}
          >
            <div className="text-black d-flex align-items-center gap-2 svg-20">
              Modify
            </div>
          </Menu.Item>
        )}

        {/* ✅ Modify Guard (Duplicate) */}
        {USER_PERMISSIONS.MODIFY && (
          <Menu.Item
            key="2"
            onClick={() => {
              saveScrollPositions();
              navigate(`/products/duplicate/${data._id}`);
            }}
          >
            <div className="text-black d-flex align-items-center gap-2 svg-20">
              Duplicate
            </div>
          </Menu.Item>
        )}
        {USER_PERMISSIONS.READ && (
          <Menu.Item
            key="export"
            onClick={() => {
              if (exporting) return;
              setExporting(true);
              dispatch(
                exportProductVariantsAPI({
                  id: data._id,
                  onSuccess: () => {
                    queryClient.setQueryData(
                      ["product", productId],
                      (prev: any) => ({
                        ...prev,
                        export_process_batch: {
                          status: "in_queue",
                        },
                      }),
                    );
                    setExporting(false);
                  },
                  onError: () => {
                    setExporting(false);
                  },
                }),
              );
            }}
          >
            Export
          </Menu.Item>
        )}

        {USER_PERMISSIONS.MODIFY && (
          <Menu.Item
            key="import"
            onClick={() => {
              saveScrollPositions();
              navigate(`/products/import-variants/${data._id}`);
            }}
          >
            Import
          </Menu.Item>
        )}

        {USER_PERMISSIONS.MODIFY && (
          <Menu.Item
            key="regenerate"
            onClick={() => setProductToRegenrate(data)}
          >
            Regenerate Variants
          </Menu.Item>
        )}

        {/* ✅ Remove Guard */}
        {USER_PERMISSIONS.REMOVE && !isDefaultProduct && (
          <Menu.Item key="3" onClick={() => setIdToDelete(data._id)}>
            <div className="text-danger d-flex align-items-center gap-2 svg-20">
              Remove
            </div>
          </Menu.Item>
        )}
      </Menu>
    );
  };

  const MoreOptionsMenu = (data: any) => {
    return (
      <Menu>
        {/* ✅ Modify Guard */}
        {USER_PERMISSIONS.MODIFY && (
          <Menu.Item
            key="1"
            onClick={() => {
              edit(data);
            }}
          >
            <div className="text-black d-flex align-items-center gap-2 svg-20">
              Modify
            </div>
          </Menu.Item>
        )}

        {/* ✅ Modify Guard (Duplicate Variant) */}
        {/* {USER_PERMISSIONS.MODIFY && (
          <Menu.Item
            key="2"
            onClick={() => {
              duplicateVariant(data);
            }}
          >
            <div className="text-black d-flex align-items-center gap-2 svg-20">
              Duplicate
            </div>
          </Menu.Item>
        )} */}

        {/* ✅ Remove Guard */}
        {USER_PERMISSIONS.REMOVE && (
          <Menu.Item key="3" onClick={() => setVariantIdToDelete(data._id)}>
            <div className="text-danger d-flex align-items-center gap-2 svg-20">
              Remove
            </div>
          </Menu.Item>
        )}
      </Menu>
    );
  };

  const handleTableChange = (pagination: any, filters: any, sorter: any) => {
    const order =
      sorter.order === "ascend"
        ? "asc"
        : sorter.order === "descend"
        ? "desc"
        : null;
    setSorterState({ field: sorter.field, order: order as any });
  };

  const renderImageBlock = (title: string, imageUrl: string) => (
    <div className="d-flex flex-column gap-2">
      <label className="custom-label mb-0 ff text-949494">{title}</label>
      <div className="image-viewer-100 product-compact-viewer position-relative rounded-3 overflow-hidden">
        <Image
          src={imageUrl ? getFullAwsUrl(imageUrl) : placeHolderImage}
          preview={imageUrl ? { src: getFullAwsUrl(imageUrl) } : false}
          fallback={placeHolderImage}
          className="rounded-3 overflow-hidden border bg-white w-100 h-100 object-fit-cover"
        />
      </div>
    </div>
  );

  const handleToggleConfirm = () => {
    setToggling(true);
    const payload = {
      variant_id: toggleStatusSettings.id,
      is_active: toggleStatusSettings.toActivate ? true : false,
    };
    dispatch(
      updateVariantAPI({
        variant_id: toggleStatusSettings.id,
        payload,
        onSuccess: (data: any) => {
          setToggling(false);
          setToggleStatusSettings({
            isModalOpen: false,
            id: "",
            toActivate: false,
          });
          const updatedVariant = data.variant || data;
          // Update query cache with updated variant
          queryClient.setQueryData(
            ["variants", productId, variantQueryFilters],
            (old: any) => {
              if (!old) return old;
              return {
                ...old,
                pages: old.pages.map((page: any) => ({
                  ...page,
                  variants: (page.variants || []).map((variant: any) => {
                    if (variant._id === updatedVariant._id) {
                      return updatedVariant;
                    }
                    return variant;
                  }),
                })),
              };
            },
          );
        },
        onError: () => {
          setToggling(false);
        },
      }),
    );
  };

  const handleCloseToggleModal = () => {
    setToggleStatusSettings({
      isModalOpen: false,
      id: "",
      toActivate: false,
    });
  };

  const handleAdd = () => {
    // 1. Prevent adding if another row is already being edited (optional safety)
    if (editingKey !== "") {
      message.warning(
        "Please save or cancel the current edit before adding a new variant.",
      );
      return;
    }

    // 2. Generate a temporary ID
    const tempId = `NEW_${Date.now()}`;

    // 3. Create the empty variant object
    // Initialize fields as null or empty strings so placeholders show up
    const newVariant = {
      _id: tempId,
      product_code: "",
      width: null,
      height: null,
      depth: null,
      length: null,
      thickness: null,
      finish: null,
      color: null,
      secondary_finish: null,
      secondary_color: null,
      weight: null,
      is_active: true, // Default new rows to active
    };

    // 4. Add to query cache at the beginning of first page
    queryClient.setQueryData(
      ["variants", productId, variantQueryFilters],
      (old: any) => {
        if (!old) return old;
        return {
          ...old,
          pages: old.pages.map((page: any, pageIndex: number) => {
            if (pageIndex === 0) {
              return {
                ...page,
                variants: [newVariant, ...(page.variants || [])],
              };
            }
            return page;
          }),
        };
      },
    );

    // 5. Trigger Edit Mode immediately
    form.setFieldsValue(newVariant);
    setEditingKey(tempId);
  };

  const handleProductDeleteConfirm = () => {
    if (!idToDelete) return;

    setDeleting(true);

    dispatch(
      deleteProductAPI({
        id: idToDelete,
        onSuccess: () => {
          sessionStorage.removeItem(PRODUCT_FILTER_KEY);
          sessionStorage.setItem(PRODUCT_REFETCH_KEY, "1");
          setDeleting(false);
          navigate(`/products`);
          setIdToDelete(null);
        },
        onError: () => {
          setDeleting(false);
        },
      }),
    );
  };

  const handleCloseProductDeleteModal = () => {
    setIdToDelete(null);
  };

  return USER_PERMISSIONS.READ ? (
    isMount ? (
      <FullScreenLoader />
    ) : (
      <>
        <div
          ref={scrollableDivRef}
          className="page-content h-100 px-40 py-40 bg-light d-flex flex-column"
        >
          {/* Header and Details Card (Unchanged) */}
          <div className="mb-3 d-flex align-items-center justify-content-between gap-3 flex-shrink-0">
            <div className="d-flex align-items-center gap-2">
              <span
                className="cursor-pointer svg-24"
                onClick={() => {
                  navigate(`/products?activeTab=${fromView}`);
                }}
              >
                {svg?.backArrow || "←"}
              </span>
              <h2 className="fs-36 fw-bold page-title mb-0">Product Details</h2>
            </div>
            <Dropdown
              overlay={() => MainMoreOptionsMenu(productData)}
              trigger={["click"]}
              placement="bottomRight"
              className="ms-md-0 ms-auto"
            >
              <span className="cursor-pointer svg-46">
                {svg?.tableMoreOption}
              </span>
            </Dropdown>
          </div>

          {fromView !== "list" ? null : loading ? (
            <div className="text-center p-5">
              <Spin size="large" />
            </div>
          ) : (
            <div className="card border-0 shadow-sm rounded-4 p-md-4 p-3 mb-4 flex-shrink-0">
              <Row gutter={[24, 24]}>
                {/* Left Column (Metadata Fields) */}
                <Col xl={12} span={24}>
                  <Row gutter={[24, 16]}>
                    {/* Product Name */}
                    <Col md={12} span={24}>
                      <label className="custom-label mb-0 ff text-949494">
                        Product Name
                      </label>
                      <div className="mb-0 fs-6 fw-semibold d-block ">
                        {productData?.name || "-"}
                      </div>
                    </Col>

                    {/* Type */}
                    <Col md={6} span={24}>
                      <label className="custom-label mb-0 ff text-949494">
                        Type
                      </label>
                      <div className="mb-0 fs-6 fw-semibold d-block ">
                        {PRODUCT_TYPE_META[productData?.product_type]?.label ||
                          productData?.product_type ||
                          PRODUCT_TYPE_META.default.label}
                      </div>
                    </Col>
                    <Col md={6} span={24}>
                      <label className="custom-label mb-0 ff text-949494">
                        Product Code Prefix
                      </label>
                      <div className="mb-0 fs-6 fw-semibold d-block ">
                        {productData?.prefix || "-"}
                      </div>
                    </Col>

                    {/* Description */}
                    <Col span={24}>
                      <label className="custom-label mb-0 ff text-949494">
                        Description
                        <span className="text-secondary fs-6 fw-normal">
                          {" "}
                          (Optional)
                        </span>
                      </label>
                      {productData?.description ? (
                        <DescriptionWithShowMore
                          description={productData?.description}
                          className="mb-0 fs-6 fw-semibold d-block"
                        />
                      ) : (
                        "-"
                      )}
                    </Col>

                    {/* Storage Type */}
                    <Col md={12} span={24}>
                      <label className="custom-label mb-0 ff text-949494">
                        Storage Type
                      </label>
                      <div className="mb-0 fs-6 fw-semibold d-block ">
                        {productData?.master_product_populated?.length > 0
                          ? productData.master_product_populated
                              .map((d: any) => d.name)
                              .join(", ")
                          : "-"}
                      </div>
                    </Col>

                    {/* Category */}
                    <Col md={12} span={24}>
                      <label className="custom-label mb-0 ff text-949494">
                        Category
                      </label>
                      <div className="mb-0 fs-6 fw-semibold d-block ">
                        {productData?.categories_variants_wise?.length > 0
                          ? productData.categories_variants_wise
                              .map((c: any) => c.name)
                              .join(", ")
                          : "-"}
                      </div>
                    </Col>
                  </Row>
                </Col>

                {/* Right Column (Images) */}
                <Col xl={12} lg={18} span={24}>
                  <Row gutter={[24, 24]}>
                    <Col lg={8} md={8} sm={12} xs={24}>
                      {renderImageBlock(
                        "Pre-Galvanised (Default)",
                        productData?.pre_galvanised_image,
                      )}
                    </Col>
                    <Col lg={8} md={8} sm={12} xs={24}>
                      {renderImageBlock(
                        "Hot Dip Galvanised",
                        productData?.hot_dip_galvanised_image,
                      )}
                    </Col>
                    <Col lg={8} md={8} sm={12} xs={24}>
                      {renderImageBlock(
                        "Powder Coated",
                        productData?.powder_coated_image,
                      )}
                    </Col>
                  </Row>
                </Col>

                {/* Bottom Row (Full Width) */}
                <Col span={24}>
                  <Row gutter={[24, 16]}>
                    {/* Product Type */}
                    <Col xl={6} md={12} span={24}>
                      <label className="custom-label mb-0 ff text-949494">
                        Product Type
                      </label>
                      <div className="mb-0 fs-6 fw-semibold d-block ">
                        {productData?.component_type?.name ?? "-"}
                      </div>
                    </Col>

                    {/* Department */}
                    <Col xl={6} md={12} span={24}>
                      <label className="custom-label mb-0 ff text-949494">
                        Department
                        <span className="text-secondary fs-6 fw-normal">
                          {" "}
                          (Optional)
                        </span>
                      </label>
                      <div className="mb-0 fs-6 fw-semibold d-block ">
                        {productData?.departments?.length > 0
                          ? productData.departments
                              .map((d: any) => d.name)
                              .join(", ")
                          : "-"}
                      </div>
                    </Col>

                    {/* Machine */}
                    <Col xl={6} md={12} span={24}>
                      <label className="custom-label mb-0 ff text-949494">
                        Machine
                        <span className="text-secondary fs-6 fw-normal">
                          {" "}
                          (Optional)
                        </span>
                      </label>
                      <div className="mb-0 fs-6 fw-semibold d-block ">
                        {productData?.machines?.length > 0
                          ? productData.machines
                              .map((m: any) => m.name)
                              .join(", ")
                          : "-"}
                      </div>
                    </Col>

                    {/* Product For */}
                    <Col xl={6} md={12} span={24}>
                      <label className="custom-label mb-0 ff text-949494">
                        Product For
                      </label>
                      <div className="mb-0 fs-6 fw-semibold d-block ">
                        {productData?.product_for?.length > 0
                          ? productData.product_for
                              .map((p: any) => getDecryptedName(p))
                              .join(", ")
                          : "All"}
                      </div>
                    </Col>
                  </Row>
                </Col>

                <Col span={24}>
                  <Row gutter={[24, 16]}>
                    <Col xl={6} md={12} span={24}>
                      <label className="custom-label mb-0 ff text-949494">
                        Minimum Order Quantities
                      </label>
                      <div className="mb-0 fs-6 fw-semibold d-block ">
                        {productData?.minQuoteQty ?? "-"}
                      </div>
                    </Col>
                    <Col xl={6} md={12} span={24}>
                      <label className="custom-label mb-0 ff text-949494">
                        Maximum Order Quantities
                      </label>
                      <div className="mb-0 fs-6 fw-semibold d-block ">
                        {productData?.maxQuoteQty ?? "-"}
                      </div>
                    </Col>
                    <Col xl={6} md={12} span={24}>
                      <label className="custom-label mb-0 ff text-949494">
                        Product Tags
                      </label>
                      <div className="mb-0 fs-6 fw-semibold d-flex flex-wrap gap-2 align-items-center">
                        {productData?.product_tags?.length > 0
                          ? productData.product_tags.map((t: any) => (
                              <Tag
                                className="py-1 px-2 rounded-3 fs-6 m-0 mw-100 text-wrap"
                                color={t.color}
                              >
                                {t.name}
                              </Tag>
                            ))
                          : "-"}
                      </div>
                    </Col>
                    <Col xl={6} md={12} span={24}>
                      <label className="custom-label mb-0 ff text-949494">
                        Standard/Non-standard Required?
                      </label>
                      <div className="mb-0 fs-6 fw-semibold d-block ">
                        {productData?.is_standard ? "Yes" : "No"}
                      </div>
                    </Col>
                    <Col xl={6} md={12} span={24}>
                      <label className="custom-label mb-0 ff text-949494">
                        Min & Max Coil Metreage Required?
                      </label>
                      <div className="mb-0 fs-6 fw-semibold d-block ">
                        {productData?.coil_meterage?.is_active ? "Yes" : "No"}
                      </div>
                    </Col>
                    <Col xl={6} md={12} span={24}>
                      <label className="custom-label mb-0 ff text-949494">
                        Label In Qty
                      </label>
                      <div className="mb-0 fs-6 fw-semibold d-block ">
                        {productData?.labelInQuotation ?? "Qty"}
                      </div>
                    </Col>
                    <Col xl={6} md={12} span={24}>
                      <label className="custom-label mb-0 ff text-949494">
                        Is Drawing Required?
                      </label>
                      <div className="mb-0 fs-6 fw-semibold d-block ">
                        {productData?.is_drawing_require ? "Yes" : "No"}
                      </div>
                    </Col>
                    <Col xl={6} md={12} span={24}>
                      <label className="custom-label mb-0 ff text-949494">
                        Is Factory Assembled Drawing Required?
                      </label>
                      <div className="mb-0 fs-6 fw-semibold d-block">
                        {productData?.assembly_drawing_require ? "Yes" : "No"}
                      </div>
                    </Col>
                    {productData?.assembly_drawing_require &&
                      productData?.assembly_drawing_doc && (
                        <Col span={24} className="mt-2">
                          <label className="custom-label mb-2 ff text-949494">
                            Factory Assembled Drawing File
                          </label>
                          <div
                            className="d-flex align-items-center justify-content-between p-3 bg-white border rounded-3 shadow-sm cursor-pointer"
                            onClick={() => {
                              setPreviewFile(
                                productData?.assembly_drawing_doc,
                              );
                              setPreviewModalOpen(true);
                            }}
                          >
                            <div className="d-flex align-items-center gap-2 text-truncate me-3">
                              <PaperClipOutlined className="text-primary fs-18" />
                              <span
                                className="fw-semibold text-dark text-truncate fs-14"
                                title={
                                  getFileNameFromUrl(
                                    productData?.assembly_drawing_doc || "",
                                  )
                                }
                              >
                                {getFileNameFromUrl(
                                  productData?.assembly_drawing_doc || "",
                                ) || "Factory Assembled Drawing"}
                              </span>
                            </div>
                            <div className="d-flex align-items-center gap-2 flex-shrink-0">
                              <Button
                                type="text"
                                icon={svg?.viewIcon}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setPreviewFile(
                                    productData?.assembly_drawing_doc,
                                  );
                                  setPreviewModalOpen(true);
                                }}
                                title="View Drawing"
                                className="border-0 d-flex align-items-center justify-content-center svg-32 p-0"
                              />
                            </div>
                          </div>
                        </Col>
                      )}
                  </Row>
                </Col>
              </Row>
            </div>
          )}

          {fromView === "list" && (
            <div>
              <div className="customHorizontalTabs bg-white overflow-hidden border-0 rounded-3 mb-3 d-inline-block mw-100">
                <Tabs
                  // tabPosition={position}
                  activeKey={activeView}
                  onChange={(key) => {
                    setSearchParams((prev) => {
                      const nextParams = new URLSearchParams(prev);
                      nextParams.set("entity", key);
                      return nextParams;
                    });
                    // setSearchParams({entity: key})
                    setActiveView(key);
                  }}
                  items={items}
                />
              </div>
            </div>
          )}

          <div className={fromView === "project" ? "row gy-3" : ""}>
            {fromView === "project" && (
              <div
                className="col-12 col-md-auto mb-3 mb-md-0"
                style={{ minWidth: "140px" }}
              >
                <div className="bg-white border-0 shadow-sm rounded-3 p-2">
                  <div className="nav flex-column nav-pills gap-1">
                    {items.map(({ label, key }, index) => {
                      const isActive = activeView === key;
                      const isLast = index === items.length - 1;
                      return (
                        <React.Fragment key={key}>
                          <div
                            className={`nav-link rounded-2 py-2 px-3 fw-semibold cursor-pointer text-center ${
                              isActive
                                ? "active bg-dark text-white shadow-sm"
                                : "text-dark bg-light-hover"
                            }`}
                            style={{
                              cursor: "pointer",
                              transition: "all 0.15s ease",
                            }}
                            onClick={() => {
                              setSearchParams((prev) => {
                                const nextParams = new URLSearchParams(prev);
                                nextParams.set("entity", key);
                                return nextParams;
                              });
                              setActiveView(key);
                            }}
                          >
                            <span>{label}</span>
                          </div>
                          {!isLast && (
                            <hr className="my-1 border-secondary opacity-25" />
                          )}
                        </React.Fragment>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            <div
              className={fromView === "project" ? "col-12 col-md" : ""}
              style={{ minWidth: 0 }}
            >
              <div
                className={
                  activeView === ProductDetailsSections.VARIANTS
                    ? "d-block"
                    : "d-none"
                }
              >
                <div className="mb-3 d-flex flex-wrap justify-content-between align-items-center flex-shrink-0">
                  <h3 className="fw-bold mb-0 d-flex gap-2 align-items-center fs-4 fs-4-mobile">
                    Variants {variantTotal > 0 && <span>({variantTotal})</span>}{" "}
                    {productData?.export_process_batch ? (
                      <span
                        className={
                          "d-inline-flex align-items-center billStatus " +
                          exportImportStatusClasess(
                            productData?.export_process_batch?.status,
                          )
                        }
                      >
                        Export{" "}
                        {exportImportStatusLabel(
                          productData?.export_process_batch?.status,
                        )}
                      </span>
                    ) : (
                      ""
                    )}
                    {productData?.import_process_batch ? (
                      <span
                        className={
                          "d-inline-flex align-items-center billStatus " +
                          exportImportStatusClasess(
                            productData?.import_process_batch?.status,
                          )
                        }
                      >
                        {getProductImportActionLabel(
                          productData?.import_process_batch,
                        )}{" "}
                        {exportImportStatusLabel(
                          productData?.import_process_batch?.status,
                        )}
                      </span>
                    ) : (
                      ""
                    )}
                  </h3>
                  <div
                    className={`d-flex flex-md-nowrap flex-wrap gap-sm-3 gap-2 justify-content-end align-items-center ms-auto order-sm-2 order-3`}
                  >
                    <div
                      className={`pageSearch mw-100 position-relative d-inline-block mt-sm-0 mt-2`}
                    >
                      <input
                        type="text"
                        value={search}
                        className="form-control form-control-lg"
                        onChange={searchBarHandler}
                        placeholder="Search"
                      />
                      {search?.length ? (
                        <span
                          className="cursor-pointer"
                          onClick={searchBarClean}
                        >
                          {svg?.searchClear}
                        </span>
                      ) : (
                        <span>{svg?.search}</span>
                      )}
                    </div>
                  </div>
                  {/* {USER_PERMISSIONS.ADD && (
            <span
              className="text-primary fw-bold cursor-pointer"
              onClick={() => handleAdd()}
            >
              + Add
            </span>
          )} */}
                </div>

                <div className="card page-table-card variantsTableCard border-0 shadow rounded-4">
                  <div
                    className="card-body overflow-y-auto h-100 p-0"
                    id="variantScrollableDiv"
                  >
                    {/* ✅ WRAPPED IN FORM */}
                    <Form
                      form={form}
                      component={false}
                      onValuesChange={(changedValues) => {
                        // Check if 'finish' was the field that changed
                        if (changedValues.finish) {
                          const newFinish = changedValues.finish;

                          // Check if the new finish is Powder Coated
                          const isPowderCoated =
                            newFinish &&
                            newFinish.replace(/_/g, " ").toLowerCase() ===
                              ProductFinishEnum.POWDER_COATED;

                          // If NOT Powder Coated, clear the 'color' field
                          if (!isPowderCoated) {
                            form.setFieldsValue({ color: null });
                          }
                        }

                        // Check if 'secondary_finish' was the field that changed
                        if (changedValues.secondary_finish) {
                          const newFinish = changedValues.secondary_finish;

                          const isPowderCoated =
                            newFinish &&
                            newFinish.replace(/_/g, " ").toLowerCase() ===
                              ProductFinishEnum.POWDER_COATED;

                          if (!isPowderCoated) {
                            form.setFieldsValue({ secondary_color: null });
                          }
                        }
                      }}
                    >
                      {variantLoading ? (
                        <div className="text-center p-5">
                          <Spin size="large" />
                        </div>
                      ) : variantsList.length === 0 ? (
                        <div className="p-4">
                          <NoDataMessage
                            icon={svg?.noProductsIcon}
                            text="No variants found for this product."
                          />
                        </div>
                      ) : (
                        <InfiniteScroll
                          dataLength={variantsList.length}
                          next={handleLoadMoreVariants}
                          hasMore={variantHasMore}
                          loader={
                            isFetchingNextVariantsPage ? (
                              <div className="text-center p-2">
                                <Spin />
                              </div>
                            ) : null
                          }
                          scrollableTarget="variantScrollableDiv"
                          style={{ height: "100%", overflow: "visible" }}
                        >
                          <Table
                            // ✅ ADDED Components Prop
                            components={{
                              body: {
                                cell: EditableCell,
                              },
                            }}
                            dataSource={variantsList}
                            scroll={{ x: "max-content" }}
                            pagination={false}
                            columns={mergedColumns}
                            onChange={handleTableChange}
                            rowKey={(record) => record._id}
                            className="variant-edit-table bg-white"
                            bordered
                          />
                        </InfiniteScroll>
                      )}
                    </Form>
                  </div>
                </div>
              </div>

              <div
                className={
                  activeView === ProductDetailsSections.PARTS
                    ? "d-block"
                    : "d-none"
                }
              >
                {visitedTabs.includes(ProductDetailsSections.PARTS) && (
                  <ProductParts productId={productId} />
                )}
              </div>

              <div
                className={
                  activeView === ProductDetailsSections.COMPONENTS
                    ? "d-block"
                    : "d-none"
                }
              >
                {visitedTabs.includes(ProductDetailsSections.COMPONENTS) && (
                  <ProductComponents productId={productId} />
                )}
              </div>

              <div
                className={
                  activeView === ProductDetailsSections.PROCESS
                    ? "d-block"
                    : "d-none"
                }
              >
                {visitedTabs.includes(ProductDetailsSections.PROCESS) && (
                  <ProductProcesses productId={productId} />
                )}
              </div>
              <div
                className={
                  activeView === ProductDetailsSections.BOM
                    ? "d-block"
                    : "d-none"
                }
              >
                {visitedTabs.includes(ProductDetailsSections.BOM) &&
                  (productData?.bom_id ? (
                    <ViewProductBOM
                      bomId={productData?.bom_id}
                      isEmbedded={true}
                      onDeleteSuccess={() => refetchProduct()}
                    />
                  ) : (
                    <div className="card page-table-card variantsTableCard border-0 shadow rounded-4 p-4">
                      <NoDataMessage
                        icon={svg?.noProductsIcon}
                        text="No BOM generated for this product."
                      />
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </div>

        {editVariantModal && (
          <EditVariantModal
            visible={editVariantModal}
            onClose={() => setEditVariantModal(false)}
            isEditData={editVariantModalData}
            productId={productData?._id}
            productData={productData}
            categoriesOptions={categories}
            machinesOptions={machines}
            framePartOptions={framePartOptions}
            activeAdditionalFields={activeAdditionalFields}
            onSuccess={(data: any) => {
              // Update query cache with updated variant
              queryClient.setQueryData(
                ["variants", productId, variantQueryFilters],
                (old: any) => {
                  if (!old) return old;
                  return {
                    ...old,
                    pages: old.pages.map((page: any) => ({
                      ...page,
                      variants: (page.variants || []).map((item: any) =>
                        item._id === editVariantModalData?._id ? data : item,
                      ),
                    })),
                  };
                },
              );
              setEditingKey("");
              setEditVariantModal(false);
              setEditVariantModalData(null);
            }}
          />
        )}
        {/* Confirmation Modals (Unchanged) */}
        <CommonConfirmationModal
          open={toggleStatusSettings.isModalOpen}
          title={
            toggleStatusSettings.toActivate
              ? "Active Product Variant?"
              : "Inactive Product Variant?"
          }
          subTitle={`Are you sure you want to mark this variant as ${
            toggleStatusSettings.toActivate ? "active" : "inactive"
          }?`}
          loading={toggling}
          onSuccess={handleToggleConfirm}
          onClose={handleCloseToggleModal}
          confirmButtonColorClass={
            toggleStatusSettings.toActivate ? "btn-primary" : "btn-red"
          }
          confirmButtonText={
            toggleStatusSettings.toActivate ? "ACTIVE" : "INACTIVE"
          }
        />
        <CommonConfirmationModal
          open={variantIdToDelete != null}
          title="Remove Variant?"
          subTitle="Removing this variant will delete it from the Product list. This action cannot be undone."
          loading={deleting}
          onSuccess={handleDeleteConfirm}
          onClose={handleCloseDeleteModal}
        />
        <CommonConfirmationModal
          open={idToDelete != null}
          title="Remove Product, Are You Sure?"
          subTitle="Removing this Product will delete it from the list, and it will no longer be available for selection. Please confirm before proceeding."
          loading={deleting}
          onSuccess={handleProductDeleteConfirm}
          onClose={handleCloseProductDeleteModal}
        />
        <CommonConfirmationModal
          open={productToRegenrate != null}
          title={`Regenerate Variants of Product “${productToRegenrate?.name}”, Are You Sure?`}
          subTitle={`This action will regenerate all variants for the product “${productToRegenrate?.name}” based on the added variables. Existing variants may be updated or replaced during this process. Please proceed only if you want to refresh the product variants.`}
          loading={regenerating}
          onSuccess={() => {
            setRegenerating(true);
            dispatch(
              regenerateVariantsAPI({
                payload: {
                  product_id: productToRegenrate._id,
                },
                onSuccess: () => {
                  sessionStorage.setItem(PRODUCT_REGENERATE_REFETCH_KEY, "1");
                  setRegenerating(false);
                  setProductToRegenrate(null);
                  queryClient.invalidateQueries({
                    queryKey: ["product", productId],
                  });
                },
                onError: () => {
                  setRegenerating(false);
                },
              }),
            );
          }}
          onClose={() => {
            setProductToRegenrate(null);
          }}
          confirmButtonText="REGENERATE"
          confirmButtonColorClass="btn-primary"
        />
        {previewModalOpen && previewFile && (
          <CommonFilePreviewModal
            open={previewModalOpen}
            file={previewFile}
            onClose={() => {
              setPreviewModalOpen(false);
              setPreviewFile(null);
            }}
          />
        )}
      </>
    )
  ) : authLoading ? (
    <FullScreenLoader />
  ) : (
    <NoPermissionPage />
  );
};

export default ProductDetails;
